import React from 'react';

const PrivacyPage = () => {
  return (
    <div className="privacy-page">
      <div className="container">
        <div className="page-header">
          <h1>Privacy Policy</h1>
          <p>Last updated: January 2026</p>
        </div>

        <div className="privacy-content">
          <section className="privacy-section">
            <h2>1. Introduction</h2>
            <p>
              Zed Scholar ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, and protect your personal 
              information when you use our educational platform.
            </p>
          </section>

          <section className="privacy-section">
            <h2>2. Information We Collect</h2>
            <h3>Personal Information</h3>
            <p>When you register for our service, we collect:</p>
            <ul>
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Educational level (Grade 7, Grade 12)</li>
              <li>Promo code usage (if applicable)</li>
              <li>Payment information (processed securely via mobile money)</li>
            </ul>

            <h3>Usage Data</h3>
            <p>We automatically collect information about your interaction with our platform:</p>
            <ul>
              <li>Pages visited and time spent</li>
              <li>Study progress and test scores</li>
              <li>Download history</li>
              <li>AI assistant interactions and queries</li>
              <li>Device and browser information</li>
              <li>IP address and location data</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul>
              <li>Provide and maintain our educational services</li>
              <li>Process payments and manage subscriptions</li>
              <li>Track your learning progress and exam performance</li>
              <li>Personalize your learning experience</li>
              <li>Provide AI-powered tutoring assistance</li>
              <li>Communicate with you about your account and updates</li>
              <li>Improve our platform and services</li>
              <li>Ensure platform security and prevent fraud</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>4. Information Sharing</h2>
            <p>We do not sell your personal information. We may share your data only with:</p>
            
            <h3>Service Providers</h3>
            <ul>
              <li>Firebase (for database, authentication, and hosting)</li>
              <li>Cloudinary (for file storage and media delivery)</li>
              <li>Mobile money providers (Airtel Money, MTN Mobile Money, Zamtel Money)</li>
              <li>Google Analytics (for platform improvement)</li>
              <li>OpenAI/Gemini (for AI assistant functionality)</li>
            </ul>

            <h3>Legal Requirements</h3>
            <p>We may disclose your information if required by law or to:</p>
            <ul>
              <li>Comply with legal obligations</li>
              <li>Protect our rights and property</li>
              <li>Prevent fraud or ensure platform security</li>
              <li>Protect the safety of our users</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>5. Data Security</h2>
            <p>
              We implement appropriate security measures to protect your personal information:
            </p>
            <ul>
              <li>Secure authentication and password protection</li>
              <li>Encryption of sensitive data</li>
              <li>Regular security audits and updates</li>
              <li>Limited access to personal information</li>
              <li>Secure data transmission protocols</li>
            </ul>
            <p>
              However, no method of transmission over the internet is 100% secure. 
              While we strive to protect your data, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="privacy-section">
            <h2>6. Cookies and Tracking</h2>
            <p>
              We use cookies and similar technologies to enhance your experience:
            </p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for basic platform functionality and authentication</li>
              <li><strong>Performance Cookies:</strong> Help us understand how you use our platform</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Analytics Cookies:</strong> Help us improve user experience</li>
            </ul>
            <p>
              You can control cookie settings through your browser preferences.
            </p>
          </section>

          <section className="privacy-section">
            <h2>7. Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to:
            </p>
            <ul>
              <li>Fulfill the purposes outlined in this Privacy Policy</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes and enforce our agreements</li>
              <li>Maintain accurate business records</li>
            </ul>
            <p>
              When you delete your account, we will remove or anonymize your personal 
              information within 30 days, except where required to retain it by law.
            </p>
          </section>

          <section className="privacy-section">
            <h2>8. Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Correct:</strong> Update inaccurate or incomplete information</li>
              <li><strong>Delete:</strong> Request deletion of your personal information</li>
              <li><strong>Port:</strong> Request transfer of your data to another service</li>
              <li><strong>Object:</strong> Object to processing of your information</li>
              <li><strong>Restrict:</strong> Limit how we use your information</li>
            </ul>
            <p>
              To exercise these rights, please contact us at privacy@zedscholar.zm
            </p>
            <p>
              We will respond to your request within 30 days as required by data protection regulations.
            </p>
          </section>

          <section className="privacy-section">
            <h2>9. Children's Privacy</h2>
            <p>
              Our service is designed for students under 18. We obtain parental 
              consent for collecting personal information from minors. Parents can:
            </p>
            <ul>
              <li>Review and delete their child's information</li>
              <li>Refuse to permit further collection</li>
              <li>Request details about information collected</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>10. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries 
              other than Zambia. We ensure appropriate safeguards are in place 
              to protect your data in accordance with applicable data protection laws.
            </p>
          </section>

          <section className="privacy-section">
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify 
              you of any changes by posting the new policy on our platform and 
              updating the "Last updated" date.
            </p>
          </section>

          <section className="privacy-section">
            <h2>12. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our data practices, 
              please contact us at:
            </p>
            <ul>
              <li>Email: evans@zedscholarzambia.com</li>
              <li>Phone (Primary): +260962306371</li>
              <li>Phone (Secondary): +260972553255</li>
              <li>Website: https://zedscholarzambia.com</li>
              <li>Address: Lusaka, Zambia</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
